import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export type FindingKind =
  | 'EXTERNALLY_PAID'
  | 'PARTIAL_RECOVERY'
  | 'BUCKET_MOVED'
  | 'REVERSAL_SUSPECTED'
  | 'DUES_GREW_UNEXPECTEDLY'
  /** Graviton tier: closed at EMI due 0 while the LMS still reports DPD. */
  | 'DPD_WITHOUT_EMI_DUE'

export type FindingStatus = 'OPEN' | 'ACCEPTED' | 'REJECTED' | 'INVESTIGATING'

export type SuggestedAction =
  | 'CLOSE_CASE'
  | 'ADJUST_OUTSTANDING'
  | 'OPEN_INVESTIGATION'
  | 'NO_OP'

export interface ReconciliationFinding {
  findingId: string
  caseId: string | null
  loanId: string | null
  loanAccountNo: string | null
  customerName: string | null
  kind: FindingKind
  confidence: number
  evidenceJson: string
  suggestedAction: SuggestedAction
  status: FindingStatus
  resolvedAt: string | null
  resolvedByUserId: string | null
  resolutionNote: string | null
  detectedAt: string
  sourceBatchId: string | null
  currentDpd: number | null
  currentBucket: string | null
  currentOutstanding: string | null
}

export interface ReconciliationQuery {
  status?: FindingStatus | undefined
  kind?: FindingKind | undefined
  limit?: number | undefined
  offset?: number | undefined
}

export function useFindings(query: ReconciliationQuery = {}) {
  const params: Record<string, string | number> = { limit: query.limit ?? 100 }
  if (query.offset) params.offset = query.offset
  if (query.status) params.status = query.status
  if (query.kind) params.kind = query.kind
  return useQuery({
    queryKey: ['reconciliation', 'findings', params],
    queryFn: async () => {
      const res = await api.get<ReconciliationFinding[]>('/v1/reconciliation/findings', { params })
      return res.data
    },
    refetchInterval: 10_000,
  })
}

export type ResolveAction = 'ACCEPT' | 'REJECT' | 'INVESTIGATE'

export interface ResolveRequest {
  action: ResolveAction
  note?: string | null
}

export function useResolveFinding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { findingId: string; body: ResolveRequest }) => {
      const res = await api.post<{ findingId: string; changed: boolean; action: string }>(
        `/v1/reconciliation/findings/${params.findingId}/resolve`,
        params.body,
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reconciliation', 'findings'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Manual "run now"
// ---------------------------------------------------------------------------

export interface RunNowResponse {
  batchId: string
  opened: number
  updated: number
  cured: number
  skipped: number
}

/**
 * Re-runs reconciliation against the most recent DPD_DUES batch (or a
 * specific batchId if passed). Surfaces a button on the Reconciliation page
 * so managers can verify they're seeing fresh state without re-uploading.
 */
export function useRunReconciliationNow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params?: { batchId?: string }) => {
      const res = await api.post<RunNowResponse>(
        '/v1/reconciliation/run-now',
        params?.batchId ? { batchId: params.batchId } : {},
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reconciliation', 'findings'] })
      qc.invalidateQueries({ queryKey: ['cases'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}
